/**
 * SearchModal — custom element that replaces <details-modal> + <details> + <summary>
 * for the header search popup.
 *
 * Attribute:  [open]      — present when the modal is visible
 * Class:      is-closing  — added during the close animation (CSS reads this)
 *
 * Expects inside:
 *   button.modal__toggle       — the trigger button (search icon)
 *   [role="dialog"][inert]     — the modal panel (inert when closed)
 *   .modal-overlay             — backdrop (click to close)
 *   button.modal__close-button — X button inside the panel
 */
class SearchModal extends HTMLElement {
  connectedCallback() {
    // Guard against re-running when the element is moved in the DOM
    // (e.g. header-search-bar.liquid appends the bar-modal to <body>)
    if (this._initialized) return;
    this._initialized = true;

    this._isAnimatingClose = false;
    this._onBodyClick = this._onBodyClick.bind(this);

    this.toggleBtn = this.querySelector('.modal__toggle');
    this.dialog    = this.querySelector('[role="dialog"]');
    this.overlay   = this.querySelector('.modal-overlay');

    if (!this.toggleBtn || !this.dialog) return;

    this.toggleBtn.addEventListener('click', this.onToggleClick.bind(this));

    // X button inside the modal
    this.dialog.querySelector('.modal__close-button')
      ?.addEventListener('click', () => this.close());

    // Escape key
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  isOpen() {
    return this.hasAttribute('open');
  }

  onToggleClick(event) {
    event.stopPropagation();
    this.isOpen() ? this.close() : this.open(event);
  }

  open(event) {
    if (this._isAnimatingClose) return; // don't open while close animation is still playing
    this.setAttribute('open', '');
    this.toggleBtn.setAttribute('aria-expanded', 'true');
    this.dialog.removeAttribute('inert');
    document.body.classList.add('overflow-hidden');
    document.addEventListener('click', this._onBodyClick, true);

    // Re-trigger AOS on elements that were hidden (display:none) when AOS initialised
    if (typeof AOS !== 'undefined') AOS.refresh();

    const focusTrap = this.dialog.querySelector('[tabindex="-1"]');
    const firstInput = this.dialog.querySelector('input:not([type="hidden"])');
    if (typeof trapFocus === 'function') {
      trapFocus(focusTrap, firstInput);
    } else if (firstInput) {
      requestAnimationFrame(() => firstInput.focus());
    }
  }

  close(focusToggle = true) {
    if (this._isAnimatingClose) return;
    this._isAnimatingClose = true;
    this.classList.add('is-closing');

    setTimeout(() => {
      this.classList.remove('is-closing');
      this._isAnimatingClose = false;
      this.removeAttribute('open');
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      this.dialog.setAttribute('inert', '');
      document.removeEventListener('click', this._onBodyClick, true);
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(focusToggle ? this.toggleBtn : null);
      }
    }, 300);
  }

  _onBodyClick(event) {
    // Close if click is outside the modal element, or on the backdrop overlay
    if (
      !this.contains(event.target) ||
      event.target === this.overlay ||
      event.target.classList.contains('modal-overlay')
    ) {
      this.close(false);
    }
  }
}

customElements.define('search-modal', SearchModal);
